import {UIWGamePlayerInformationView} from './UIWGamePlayerInformationView'
import {UIGamePlayersController} from '../../../../shared/scripts/ui_game/ui_game_players/UIGamePlayersController'
import {UIWGameManager} from '../UIWGameManager'
import {
    MechanicCityImprovementsState,
    MechanicCityState,
    MechanicCityWallState,
    MechanicDevelopmentCardsState,
    MechanicLargestArmyState,
    MechanicLongestRoadState,
    MechanicProgressCardsState,
    MechanicRoadState,
    MechanicSettlementState,
    MechanicSpecialBuildPhaseState,
    PlayerColor,
    TurnState,
} from '@colonist/game-states'

export class UIWGamePlayersController extends UIGamePlayersController {
    constructor(uiGameManager: UIWGameManager,
                developmentCardsState: MechanicDevelopmentCardsState | undefined,
                progressCardsState: MechanicProgressCardsState | undefined,
                settlementState: MechanicSettlementState | undefined,
                cityState: MechanicCityState | undefined,
                cityWallState: MechanicCityWallState | undefined,
                cityImprovementState: MechanicCityImprovementsState | undefined,
                roadState: MechanicRoadState | undefined,
                longestRoadState: MechanicLongestRoadState | undefined,
                largestArmyState: MechanicLargestArmyState | undefined,
                specialBuildPhaseState: MechanicSpecialBuildPhaseState | undefined) {
        super(uiGameManager, developmentCardsState, progressCardsState, settlementState, cityState, cityWallState, cityImprovementState, roadState, longestRoadState, largestArmyState, specialBuildPhaseState)

        this.createView()
    }

    private createView() {
        this.createContainers()
        this.createAllPlayers()
        this.initPlayerInformationWaitIconViews()
        this.initScrollAnimationController()
    }

    protected createContainers(): void {
        const canvas = this.uiGameManager.canvasController
        const w = canvas.chatWidth
        const h = canvas.itemSize * 4.5
        const x = canvas.appW - canvas.chatWidth
        const y = canvas.appH - h
        super.createContainers(x, y, w, h, canvas.app.stage, false)
    }

    protected onCanvasResize(): void {
        this.container.destroy({children: true})
        this.playerInformationViews = []
        this.createView()
        this.scrollToCurrentTurnPlayer(false)
        this.hideElementsForBeginnerMode()
    }

    protected createOpponentPlayerInformationViews(partitionH: number, partitionW: number, bottomPlayerColor: PlayerColor) {
        let yMultiplier = this.uiGameManager.gameState.players.length >= 4 ? 0 : (4 - this.uiGameManager.gameState.players.length)
        const newPlayerOrder = this.getPlayerOrderWhereMyColorIsLast()
        const opponentViewContainer = this.opponentsScrollbox.content
        for(const playerColor of newPlayerOrder) {
            if(playerColor == bottomPlayerColor) continue

            const player = this.uiGameManager.gameController.getPlayerState(playerColor)
            const playerState = this.uiGameManager.gameController.getPlayerState(playerColor)
            const partitionY = partitionH * yMultiplier
            const devCardsPlayerState = this.developmentCardsValidator?.getDevelopmentCardsPlayerState(playerColor)
            const progressCardsPlayerValidator = this.progressCardsValidator?.getProgressCardsPlayerValidator(playerColor)
            const settlementPlayerState = this.settlementValidator?.getSettlementPlayerState(playerColor)
            const cityPlayerState = this.cityValidator?.getCityPlayerState(playerColor)
            const cityWallPlayerState = this.cityWallValidator?.getCityWallPlayerState(playerColor)
            const cityImprovementPlayerState = this.cityImprovementsValidator?.getPlayerCityImprovementsState(playerColor)
            const roadPlayerState = this.roadValidator?.getRoadPlayerState(playerColor)
            const longestRoadPlayerState = this.longestRoadValidator?.getLongestRoadPlayerState(playerColor)
            const largestArmyPlayerState = this.largestArmyValidator?.getLargestArmyPlayerState(playerColor)

            const opponentView = new UIWGamePlayerInformationView(this.uiGameManager, player, playerState.state, partitionY, partitionW, partitionH, opponentViewContainer, false,
                devCardsPlayerState,
                progressCardsPlayerValidator,
                settlementPlayerState,
                cityPlayerState,
                cityWallPlayerState,
                cityImprovementPlayerState,
                roadPlayerState,
                longestRoadPlayerState,
                largestArmyPlayerState,
                this.specialBuildPhaseState)
            this.playerInformationViews.push(opponentView)
            yMultiplier += 1
        }
        this.opponentsScrollbox.setIsScrollable()
    }

    protected createMyPlayerInformationView(partitionH: number, partitionW: number, bottomPlayerColor: PlayerColor) {
        const bottomPlayer = this.uiGameManager.gameController.getPlayerState(bottomPlayerColor)
        const bottomPlayerState = this.uiGameManager.gameController.getPlayerState(bottomPlayerColor)
        const devCardsPlayerState = this.developmentCardsValidator?.getDevelopmentCardsPlayerState(bottomPlayerColor)
        const progressCardsPlayerValidator = this.progressCardsValidator?.getProgressCardsPlayerValidator(bottomPlayerColor)
        const settlementPlayerState = this.settlementValidator?.getSettlementPlayerState(bottomPlayerColor)
        const cityPlayerState = this.cityValidator?.getCityPlayerState(bottomPlayerColor)
        const cityWallPlayerState = this.cityWallValidator?.getCityWallPlayerState(bottomPlayerColor)
        const cityImprovementsPlayerState = this.cityImprovementsValidator?.getPlayerCityImprovementsState(bottomPlayerColor)
        const roadPlayerState = this.roadValidator?.getRoadPlayerState(bottomPlayerColor)
        const longestRoadPlayerState = this.longestRoadValidator?.getLongestRoadPlayerState(bottomPlayerColor)
        const largestArmyPlayerState = this.largestArmyValidator?.getLargestArmyPlayerState(bottomPlayerColor)

        const playerInformation = new UIWGamePlayerInformationView(this.uiGameManager, bottomPlayer, bottomPlayerState.state, 0, partitionW, partitionH * 1.5, this.playerContainer, true,
            devCardsPlayerState,
            progressCardsPlayerValidator,
            settlementPlayerState,
            cityPlayerState,
            cityWallPlayerState,
            cityImprovementsPlayerState,
            roadPlayerState,
            longestRoadPlayerState,
            largestArmyPlayerState,
            this.specialBuildPhaseState)
        this.playerInformationViews.push(playerInformation)
    }

    protected scrollToCurrentTurnPlayer(shouldAnimateScroll: boolean = true) {
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.Dice) return
        if(!this.opponentsScrollbox.isScrollable) return

        const playerView = this.getPlayerInformationViewWithColor(this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
        if(playerView == undefined) return

        const playerIndex = this.playerInformationViews.indexOf(playerView)
        const scrollIndex = playerIndex <= 0 ? 0 : playerIndex - 1
        const scrollDestination = this.uiGameManager.canvasController.itemSize * scrollIndex

        if(shouldAnimateScroll) {
            this.scrollAnimationController.animate(scrollDestination)
            return
        }
        this.opponentsScrollbox.scrollTop = scrollDestination
    }
}
